import {DateTime} from "luxon";
import {DurationProps} from "../../types-enums/activity-common-properties";

/**
 * Converts a DateTime object to a flight date string in the format YYYY-MM-DD.
 *
 * @param {Object} date - The DateTime object containing year, month, and day fields.
 * @param {number} date.year - The year part of the date.
 * @param {number} date.month - The month part of the date.
 * @param {number} date.day - The day part of the date.
 * @returns {string} A formatted string representing the date in the format YYYY-MM-DD
 *                   with leading zeros for single-digit day and month values.
 */
export const convertDateTimeToFlightDate = (date: DateTime): string => {
    const {year, day, month} = date;
    const leadingZeroDay = day <= 9 ? `0${day}` : day;
    const leadingZeroMonth = month <= 9 ? `0${month}` : month;
    return `${year}-${leadingZeroMonth}-${leadingZeroDay}`;
};

/**
 * Converts a given date in milliseconds to a string formatted as a flight date.
 *
 * The format of the resulting date string is `YYYY-MM-DD`, where:
 * - `YYYY` represents the 4-digit year.
 * - `MM` represents the 2-digit month (with a leading zero if necessary).
 * - `DD` represents the 2-digit day of the month (with a leading zero if necessary).
 *
 * @param {number} dateMillis - The date in milliseconds since the UNIX epoch (January 1, 1970).
 * @returns {string} The formatted flight date as a string.
 */
export const convertDateInMillisecondsToFlightDate = (dateMillis: number): string => {
    const date = DateTime.fromMillis(dateMillis)
    const {year, day, month} = date
    const leadingZeroDay = day <= 9 ? `0${day}` : day
    const leadingZeroMonth = month <= 9 ? `0${month}` : month
    return `${year}-${leadingZeroMonth}-${leadingZeroDay}`
}

/**
 * Converts a given date to a backend-compatible string representation.
 *
 * @param {number | DateTime} date - The date to convert, which can be either a Unix timestamp in milliseconds
 *                                    or a Luxon DateTime object.
 * @returns {string} The ISO 8601 string representation of the given date in UTC format.
 */
export const convertDateToBackendStringDate = (date: number | DateTime): string => {
    if (typeof date === 'number') return DateTime.fromMillis(date).toJSDate().toISOString()
    return date.toJSDate().toISOString()
}

/**
 * Converts a backend-provided string representation of a date
 * to a DateTime object.
 *
 * @param {string | null} date - The string representation of the date
 * in ISO format, or null.
 * @returns {DateTime | null} The DateTime object if the input date
 * is a valid string, otherwise returns null.
 */
export const convertBackendStringDateToDateTime = (date: string | null): DateTime | null => {
    if (date === null) return date
    return DateTime.fromISO(date)
}

/**
 * Converts input parameters into a duration object containing dates and/or duration.
 *
 * @param {string | null} startDate - The start date in string format. Null if not available.
 * @param {string | null} endDate - The end date in string format. Null if not available.
 * @param {number | null} daysOrNights - The duration in days or nights. Null if not available.
 * @returns {DurationProps | null} A duration object containing converted date properties or null if inputs are invalid.
 */
export const convertToDurationProps = (startDate: string | null, endDate: string | null, daysOrNights: number | null): DurationProps | null => {
    let duration: DurationProps | null = null
    if (startDate !== null && endDate !== null) {
        const convertedStartDate = convertBackendStringDateToDateTime(startDate)
        const convertedEndDate = convertBackendStringDateToDateTime(endDate)
        if (convertedStartDate !== null && convertedEndDate !== null) {
            duration = {
                dates: {
                    startDate: convertedStartDate,
                    endDate: convertedEndDate
                },
                daysOrNights: null
            }
        }
    } else {
        if (daysOrNights !== null) duration = {
            dates: null,
            daysOrNights
        }
    }

    return duration
}